import { useState, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import EthereumProvider from '@walletconnect/ethereum-provider';
import { getCurrentNetwork } from '@/lib/ethereum-config';

export type ProviderType = 'metamask' | 'walletconnect';

export function useEIP1193Provider() {
  const [provider, setProvider] = useState<any>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [type, setType] = useState<ProviderType | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const connectLockRef = useRef<boolean>(false);

  // @ts-ignore
  const injectedEthereum: any | undefined = (typeof window !== 'undefined' ? (window as any).ethereum : undefined);

  // Connect MetaMask
  const connectMetaMask = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      if (typeof window === 'undefined' || !injectedEthereum) {
        setError('MetaMask not found');
        setConnecting(false);
        return;
      }
      const accounts = await injectedEthereum.request({ method: 'eth_requestAccounts' });
      setProvider(injectedEthereum);
      setAddress(accounts[0]);
      setType('metamask');
    } catch (e: any) {
      setError(e.message || 'Failed to connect MetaMask');
    } finally {
      setConnecting(false);
    }
  }, [injectedEthereum]);

  // Connect WalletConnect
  const connectWalletConnect = useCallback(async () => {
    if (connectLockRef.current || connecting) return;
    connectLockRef.current = true;
    setConnecting(true);
    setError(null);
    try {
      // If there is an existing WC provider, disconnect first to avoid stale session/proposal
      if (type === 'walletconnect' && provider?.disconnect) {
        try { await provider.disconnect(); } catch {}
        setProvider(null);
        setAddress(null);
        setType(null);
      }

      const projectId = process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID || '8662c799268b9648b608b7fb736ac118';
      const network = getCurrentNetwork();
      const targetChainId = network.chainId;

      const initConfig: any = {
        projectId,
        chains: [targetChainId],
        showQrModal: true,
        // Reduce provider ping noise
        disableProviderPing: true,
        logger: 'silent',
        metadata: {
          name: 'NFTotem',
          description: 'Personalized NFT Totems',
          url: 'https://nftotem.ilyakazakov.com',
          icons: ['https://nftotem.ilyakazakov.com/favicon.ico']
        }
      };

      let wcProvider = await EthereumProvider.init(initConfig);
      try {
        await wcProvider.enable();
      } catch (e: any) {
        const msg = String(e?.message || '');
        if (/No matching key|Pending session not found|session topic/i.test(msg)) {
          // Stale session/proposal. Reset and retry once with fresh instance
          try { await wcProvider.disconnect(); } catch {}
          wcProvider = await EthereumProvider.init(initConfig);
          await wcProvider.enable();
        } else {
          throw e;
        }
      }

      // Attach no-op listeners to avoid "without any listeners" warnings
      try {
        const anyProv: any = wcProvider as any
        const safeOn = (ev: string, fn: (...args: any[]) => void) => {
          try { anyProv?.on?.(ev, fn) } catch {}
        }
        safeOn('session_request', () => {});
        safeOn('session_event', () => {});
        safeOn('session_update', () => {});
        safeOn('session_delete', () => {
          setProvider(null);
          setAddress(null);
          setType(null);
        });
        safeOn('display_uri', (_: any) => {});
        safeOn('connect', () => {});
        safeOn('disconnect', () => {
          setProvider(null);
          setAddress(null);
          setType(null);
        });
        safeOn('accountsChanged', (_: any) => {});
        safeOn('chainChanged', (_: any) => {});
      } catch {}

      const accounts = wcProvider.accounts;
      setProvider(wcProvider);
      setAddress(accounts && accounts[0] ? accounts[0] : null);
      setType('walletconnect');
    } catch (e: any) {
      setError(e.message || 'Failed to connect WalletConnect');
    } finally {
      setConnecting(false);
      connectLockRef.current = false;
    }
  }, [provider, type, connecting]);

  // Disconnect
  const disconnect = useCallback(async () => {
    if (type === 'walletconnect' && provider?.disconnect) {
      await provider.disconnect();
    }
    setProvider(null);
    setAddress(null);
    setType(null);
    setError(null);
  }, [provider, type]);

  return {
    provider,
    address,
    type,
    connecting,
    error,
    connectMetaMask,
    connectWalletConnect,
    disconnect,
  };
} 